// services/session.js
import SESSION_CONFIG from '@/config/session';
import { showWarningToast } from '@/utils/toast';

class SessionManager {
  constructor() {
    this.sessionTimeout = SESSION_CONFIG.SESSION_TIMEOUT;
    this.warningTimeout = SESSION_CONFIG.WARNING_TIMEOUT;
    this.refreshInterval = SESSION_CONFIG.REFRESH_INTERVAL;
    this.activityDebounce = SESSION_CONFIG.ACTIVITY_DEBOUNCE;
    
    // Optimized timer management
    this.timers = new Map();
    this.warningTimers = new Map();
    this.refreshTimers = new Map();
    this.validityCheckInterval = null;
    
    // State management
    this.isWarningShown = new Map(); // Track warning state per system
    this.lastActivityTime = new Map(); // Track last activity per system
    this.debounceTimers = new Map(); // Debounce timers per system
    
    // References
    this.store = null;
    this.router = null;
    
    // Performance optimizations
    this.isMobile = this.detectMobile();
    this.activityEvents = this.isMobile ? 
      [...SESSION_CONFIG.ACTIVITY_EVENTS, ...SESSION_CONFIG.MOBILE.TOUCH_EVENTS] : 
      SESSION_CONFIG.ACTIVITY_EVENTS;
  }

  detectMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  initialize(store, router) {
    try {
      this.store = store;
      this.router = router;
      this.setupActivityListeners();
      this.startSessionTimers();
      this.startValidityCheckInterval();
      console.log('Session manager initialized successfully');
    } catch (error) {
      console.error('Failed to initialize session manager:', error);
      // Don't throw error to prevent app crash
    }
  }

  setupActivityListeners() {
    try {
      // Use event delegation for better performance
      const handleActivity = this.debounceActivity.bind(this);
      
      this.activityEvents.forEach(event => {
        try {
          document.addEventListener(event, handleActivity, { 
            passive: true, 
            capture: false 
          });
        } catch (error) {
          console.warn(`Failed to add event listener for ${event}:`, error);
        }
      });

      // Handle visibility change (tab switching) - optimized
      try {
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            this.checkSessionValidity();
          }
        }, { passive: true });
      } catch (error) {
        console.warn('Failed to add visibilitychange listener:', error);
      }

      // Handle beforeunload (page refresh/close) - optimized
      try {
        window.addEventListener('beforeunload', () => {
          this.saveSessionState();
        }, { passive: true });
      } catch (error) {
        console.warn('Failed to add beforeunload listener:', error);
      }
    } catch (error) {
      console.error('Failed to setup activity listeners:', error);
    }
  }

  // Debounced activity handler to prevent excessive timer resets
  debounceActivity() {
    const currentTime = Date.now();
    
    SESSION_CONFIG.SUPPORTED_SYSTEMS.forEach(system => {
      if (this.store?.getters?.isAuthenticated(system)) {
        const lastActivity = this.lastActivityTime.get(system) || 0;
        
        // Only reset if enough time has passed since last activity
        if (currentTime - lastActivity >= this.activityDebounce) {
          this.lastActivityTime.set(system, currentTime);
          this.resetSessionTimers(system);
        }
      }
    });
  }

  startValidityCheckInterval() {
    // Periodic session validity check (less frequent for better performance)
    this.validityCheckInterval = setInterval(() => {
      this.checkSessionValidity();
    }, SESSION_CONFIG.PERFORMANCE.VALIDITY_CHECK_INTERVAL);
  }

  startSessionTimers() {
    try {
      const systems = SESSION_CONFIG.SUPPORTED_SYSTEMS;

      systems.forEach(system => {
        try {
          if (this.store?.getters?.isAuthenticated(system)) {
            this.startTimerForSystem(system);
          }
        } catch (error) {
          console.warn(`Failed to start timer for system ${system}:`, error);
        }
      });
    } catch (error) {
      console.error('Failed to start session timers:', error);
    }
  }

  startTimerForSystem(system) {
    // Clear existing timers for this system
    this.clearTimersForSystem(system);

    const sessionStartTime = this.getSessionStartTime(system);
    const currentTime = Date.now();
    const elapsedTime = currentTime - sessionStartTime;
    const remainingTime = this.sessionTimeout - elapsedTime;

    if (remainingTime <= 0) {
      // Session already expired
      this.logoutSystem(system);
      return;
    }

    // Set warning timer
    const warningTime = remainingTime - this.warningTimeout;
    if (warningTime > 0) {
      this.warningTimers.set(system, setTimeout(() => {
        this.showSessionWarning(system);
      }, warningTime));
    } else {
      // Warning time has passed, show warning immediately
      this.showSessionWarning(system);
    }

    // Set logout timer
    this.timers.set(system, setTimeout(() => {
      this.logoutSystem(system);
    }, remainingTime));

    // Set refresh timer (only if refresh is needed)
    if (remainingTime > this.refreshInterval) {
      const refreshTime = Math.min(remainingTime - this.refreshInterval, this.refreshInterval);
      this.refreshTimers.set(system, setTimeout(() => {
        this.refreshSession(system);
      }, refreshTime));
    }
  }

  resetSessionTimers(system) {
    if (this.store?.getters?.isAuthenticated(system)) {
      this.updateSessionStartTime(system);
      this.startTimerForSystem(system);
    }
  }

  clearTimersForSystem(system) {
    // Clear all timers for a specific system
    const timers = [
      this.timers.get(system),
      this.warningTimers.get(system),
      this.refreshTimers.get(system)
    ];
    
    timers.forEach(timer => {
      if (timer) clearTimeout(timer);
    });
    
    this.timers.delete(system);
    this.warningTimers.delete(system);
    this.refreshTimers.delete(system);
    this.isWarningShown.delete(system);
    this.lastActivityTime.delete(system);
    
    // Clear debounce timer
    const debounceTimer = this.debounceTimers.get(system);
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      this.debounceTimers.delete(system);
    }
  }

  clearAllTimers() {
    // Clear all timers and intervals
    this.timers.forEach(timer => clearTimeout(timer));
    this.warningTimers.forEach(timer => clearTimeout(timer));
    this.refreshTimers.forEach(timer => clearTimeout(timer));
    
    if (this.validityCheckInterval) {
      clearInterval(this.validityCheckInterval);
    }
    
    this.timers.clear();
    this.warningTimers.clear();
    this.refreshTimers.clear();
    this.isWarningShown.clear();
    this.lastActivityTime.clear();
    this.debounceTimers.clear();
  }

  getSessionStartTime(system) {
    try {
      const startTime = localStorage.getItem(`${system}_session_start`);
      return startTime ? parseInt(startTime) : Date.now();
    } catch (error) {
      console.warn(`Failed to get session start time for ${system}:`, error);
      return Date.now();
    }
  }

  updateSessionStartTime(system) {
    try {
      localStorage.setItem(`${system}_session_start`, Date.now().toString());
    } catch (error) {
      console.warn(`Failed to update session start time for ${system}:`, error);
    }
  }

  checkSessionValidity() {
    const systems = SESSION_CONFIG.SUPPORTED_SYSTEMS;
    
    systems.forEach(system => {
      if (this.store?.getters?.isAuthenticated(system)) {
        const sessionStartTime = this.getSessionStartTime(system);
        const currentTime = Date.now();
        const elapsedTime = currentTime - sessionStartTime;

        if (elapsedTime >= this.sessionTimeout) {
          this.logoutSystem(system);
        } else {
          // Restart timers with remaining time
          this.startTimerForSystem(system);
        }
      }
    });
  }

  showSessionWarning(system) {
    if (this.isWarningShown.get(system)) return;
    
    this.isWarningShown.set(system, true);
    
    const remainingTime = Math.ceil(this.warningTimeout / 1000 / 60); // Convert to minutes
    
    // Show toast notification first
    const systemDisplayNames = {
      eLearning: 'E-Learning',
      hmis: 'Doctor Bookings',
      rafiki360: 'Rafiki360',
      appeals: 'Appeals $ Donations'
    };
    const displayName = systemDisplayNames[system] || system;
    
    showWarningToast(
      `Your session in ${displayName} will expire in ${remainingTime} minutes due to inactivity.`,
      {
        timeout: 10000,
        closeOnClick: true,
        pauseOnFocusLoss: true,
        pauseOnHover: true,
        draggable: true,
        draggablePercent: 0.6,
        showCloseButtonOnHover: false,
        hideProgressBar: false,
        closeButton: "button",
        icon: true,
        rtl: false
      }
    );
    
    // Use a more user-friendly confirmation dialog
    const userConfirmed = confirm(
      `Your session in ${displayName} will expire in ${remainingTime} minutes due to inactivity.\n\n` +
      'Click OK to extend your session, or Cancel to logout now.'
    );

    if (userConfirmed) {
      this.extendSession(system);
    } else {
      this.logoutSystem(system);
    }
    
    this.isWarningShown.set(system, false);
  }

  extendSession(system) {
    this.updateSessionStartTime(system);
    this.startTimerForSystem(system);
    console.log(`Session extended for ${system}`);
  }

  async refreshSession(system) {
    try {
      // Here you would typically call an API endpoint to refresh the token
      // For now, we'll just extend the session locally
      console.log(`Refreshing session for ${system}`);
      this.extendSession(system);
    } catch (error) {
      console.error(`Failed to refresh session for ${system}:`, error);
      this.logoutSystem(system);
    }
  }

  logoutSystem(system) {
    try {
      console.log(`Logging out from ${system} due to session timeout`);

      // Show session timeout toast message
      const systemDisplayNames = {
        eLearning: 'E-Learning',
        hmis: 'Doctor Bookings',
        rafiki360: 'Rafiki360',
        appeals: 'Appeals $ Donations'
      };
      const displayName = systemDisplayNames[system] || system;
      
      showWarningToast(
        `Your session in ${displayName} has expired due to inactivity. Please log in again.`,
        {
          timeout: 5000,
          closeOnClick: true,
          pauseOnFocusLoss: true,
          pauseOnHover: true,
          draggable: true,
          draggablePercent: 0.6,
          showCloseButtonOnHover: false,
          hideProgressBar: false,
          closeButton: "button",
          icon: true,
          rtl: false
        }
      );

      this.clearTimersForSystem(system);

      if (this.store?.dispatch) {
        this.store.dispatch('logout', { system });
      }

      // Redirect to login if not already there
      if (this.router?.currentRoute?.value?.path !== '/login') {
        this.router.push('/login');
      }
    } catch (error) {
      console.error(`Failed to logout from system ${system}:`, error);
    }
  }

  saveSessionState() {
    // Save current session state before page unload
    const systems = SESSION_CONFIG.SUPPORTED_SYSTEMS;
    systems.forEach(system => {
      if (this.store?.getters?.isAuthenticated(system)) {
        this.updateSessionStartTime(system);
      }
    });
  }

  // Public methods for external use
  login(system) {
    this.updateSessionStartTime(system);
    this.startTimerForSystem(system);
  }

  logout(system = null) {
    if (system) {
      this.clearTimersForSystem(system);
    } else {
      this.clearAllTimers();
    }
  }

  getRemainingTime(system) {
    try {
      const sessionStartTime = this.getSessionStartTime(system);
      const currentTime = Date.now();
      const elapsedTime = currentTime - sessionStartTime;
      return Math.max(0, this.sessionTimeout - elapsedTime);
    } catch (error) {
      console.warn(`Failed to get remaining time for ${system}:`, error);
      return 0;
    }
  }

  isSessionExpired(system) {
    return this.getRemainingTime(system) <= 0;
  }

  // Cleanup method for component unmounting
  cleanup() {
    this.clearAllTimers();
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;
